import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { listCoalitions, listParties, searchCandidates } from '@/features/politics/api'
import { listMobilizers } from '@/features/mobilizers/api'
import { searchUnits } from '@/features/geography/api'
import { searchVoters } from '@/features/voters/api'
import { EMPTY_USER_FILTERS, listUsers } from '@/features/users/api'
import { resolveSubject, setCollection } from './api'
import { label } from './components/primitives'

/** One option as the shared picker wants it: an id, a name, and a disambiguating line. */
type Option = { value: string; label: string; sub?: string }

/**
 * How to FIND each kind of entity, by name.
 *
 * Every one of these is a SERVER search. The registers here run to five figures — a voter
 * roll is hundreds of thousands — so there is no version of this control that loads the list
 * and filters it in the browser, and the previous "paste the entity id" field was the same
 * problem wearing a different hat: it made the operator go and find a UUID somewhere else.
 *
 * ORGANISATION is deliberately absent. Nothing in this platform issues organisation ids yet,
 * so there is nothing to search; that type keeps the raw-id field, with a sentence saying so,
 * rather than a picker that would always be empty.
 */
const SEARCHERS: Record<string, (term: string) => Promise<Option[]>> = {
  CANDIDATE: async (term) => (await searchCandidates(term)).map((c) => ({
    value: c.id,
    label: c.fullName,
    sub: [label(c.seatTypeCode), c.unitLabel, c.party?.shortName ?? c.party?.name]
      .filter(Boolean).join(' · '),
  })),
  PARTY: async (term) => (await listParties({ search: term }, 0, 25)).content.map((p) => ({
    value: p.id, label: p.name, sub: p.shortName ?? p.code,
  })),
  COALITION: async (term) => (await listCoalitions({ search: term }, 0, 25)).content.map((c) => ({
    value: c.id, label: c.name, sub: c.shortName ?? c.code,
  })),
  MOBILIZER: async (term) =>
    (await listMobilizers({ q: term }, undefined, 25)).content.map((m) => ({
      value: m.id,
      label: m.fullName,
      sub: [label(m.roleCode), m.primaryUnitName].filter(Boolean).join(' · '),
    })),
  GEOGRAPHY_UNIT: async (term) => (await searchUnits(term)).map((u) => ({
    value: u.id, label: u.name, sub: `${label(u.unitType)} · ${u.code}`,
  })),
  VOTER: async (term) => (await searchVoters(term)).map((v) => ({
    value: v.id, label: v.displayName, sub: label(v.verificationStatusCode),
  })),
  USER: async (term) =>
    (await listUsers({ ...EMPTY_USER_FILTERS, q: term }, 0, 25)).content.map((u) => ({
      value: u.id, label: u.name, sub: u.phone,
    })),
}

const SLUG_TYPES = ['TOPIC', 'EXTERNAL_PERSON']
const REF_TYPES = ['CANDIDATE', 'PARTY', 'COALITION', 'ORGANISATION', 'MOBILIZER',
  'GEOGRAPHY_UNIT', 'VOTER', 'USER']
const LEGAL_BASES = ['PUBLIC_RECORD', 'OPEN_SOURCE', 'LEGITIMATE_INTEREST', 'CONSENT',
  'PARTY_MEMBER', 'CONTRACT']

/**
 * Open a file — on anybody, or on anything.
 *
 * ## Two modes, one form
 *
 * **Pick an entity** for something this platform already knows about, resolved by its row id.
 * **Name a subject** for something it does not — "the Nepal landslide", a journalist who
 * appears in none of our tables. A `TOPIC` owns no row anywhere and is identified by a slug,
 * which is why the two modes cannot be collapsed into one field.
 *
 * ## The second step is not skippable, and the third is a separate decision
 *
 * Legal basis and purpose are required before a file exists. Then — and only then — the form
 * offers to start collection, as its own act with its own purpose, because
 * `POST /subjects/{id}/collection` is what begins surveillance on a named person and the UI
 * must make that feel like a decision rather than a checkbox somebody tabbed past.
 */
export function NewTargetPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<'entity' | 'name'>('name')
  const [typeCode, setTypeCode] = useState('TOPIC')
  const [subjectRef, setSubjectRef] = useState('')
  const [entity, setEntity] = useState<Option | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [legalBasisCode, setLegalBasisCode] = useState('OPEN_SOURCE')
  const [purpose, setPurpose] = useState('')
  const [startCollection, setStartCollection] = useState(false)
  const [collectionPurpose, setCollectionPurpose] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const slugMode = mode === 'name'
  const purposeTooShort = purpose.trim().length < 20
  const collectionPurposeMissing = startCollection && collectionPurpose.trim().length < 20

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (purposeTooShort || collectionPurposeMissing) return
    if (!slugMode && !subjectRef.trim()) return
    setBusy(true)
    setError(null)
    try {
      const resolved = await resolveSubject({
        subjectTypeCode: typeCode,
        subjectRef: slugMode ? null : subjectRef.trim() || null,
        slug: slugMode ? slugify(displayName) : null,
        displayName: displayName.trim(),
        legalBasisCode,
      })
      if (startCollection) {
        // A separate call because it is a separate act — and a separately audited one.
        await setCollection(resolved.subjectId, true, collectionPurpose.trim())
      }
      navigate(`/dossier/${resolved.subjectId}`)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-3">
      <SectionHeader
        title="New target"
        description="A subject need not be a person, and need not exist anywhere else in this
                     platform."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="space-y-3 p-3">
          <div className="flex gap-1">
            {(['name', 'entity'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m)
                  setTypeCode(m === 'name' ? 'TOPIC' : 'CANDIDATE')
                  setEntity(null)
                  setSubjectRef('')
                }}
                className={`rounded border px-2 py-1 text-[12px] ${
                  mode === m ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground'}`}
              >
                {m === 'name' ? 'Name a subject' : 'Pick an entity'}
              </button>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground">
            {slugMode
              ? 'For something that owns no row in this platform — a topic, an event, a person '
                + 'who appears in none of our registers.'
              : 'For a candidate, party, coalition, mobiliser, unit, voter or user already '
                + 'recorded here. Search for them by name.'}
          </p>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Subject type
            </span>
            <select value={typeCode}
                    onChange={(e) => {
                      setTypeCode(e.target.value)
                      // A candidate id is not a party id. Keeping the previous pick across a
                      // type change is how a form submits a reference to the wrong table.
                      setEntity(null)
                      setSubjectRef('')
                    }}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              {(slugMode ? SLUG_TYPES : REF_TYPES).map((t) => (
                <option key={t} value={t}>{label(t)}</option>
              ))}
            </select>
          </label>

          {!slugMode && (SEARCHERS[typeCode] ? (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {label(typeCode)}
              </span>
              <AsyncMultiSelect
                label={label(typeCode)}
                placeholder={`Search ${label(typeCode).toLowerCase()}s by name`}
                emptyLabel="None chosen"
                selected={entity ? [{ value: entity.value, label: entity.label }] : []}
                // One subject, so the last click wins rather than accumulating.
                onChange={(next) => {
                  const chosen = next.slice(-1)[0] ?? null
                  setEntity(chosen)
                  setSubjectRef(chosen?.value ?? '')
                  // The display name is the label unless somebody has typed over it. A form
                  // that made you retype a name it had just shown you would be the same
                  // discourtesy as making you fetch the id.
                  setDisplayName((current) =>
                    current.trim() === '' || current === entity?.label
                      ? chosen?.label ?? '' : current)
                }}
                search={(term) => SEARCHERS[typeCode]!(term)}
              />
              {entity && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {/* Shown, never typed — it is what goes on the wire and what an operator
                      quotes when something goes wrong. */}
                  {entity.value}
                </span>
              )}
            </div>
          ) : (
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {label(typeCode)} id
              </span>
              <input value={subjectRef} onChange={(e) => setSubjectRef(e.target.value)}
                     required placeholder="00000000-0000-0000-0000-000000000000"
                     className="h-7 rounded border border-input bg-background px-2 font-mono
                                text-[12px]" />
              <span className="text-[10px] text-amber-600">
                Nothing in this platform issues {label(typeCode).toLowerCase()} ids yet, so
                there is nothing to search. Paste the id from wherever it is recorded — or use
                “Name a subject” and open the file as a topic instead.
              </span>
            </label>
          ))}

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Display name
            </span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                   required placeholder={slugMode ? 'Nepal landslide' : 'As it should read'}
                   className="h-7 rounded border border-input bg-background px-2 text-[12px]" />
            {slugMode && displayName && (
              <span className="font-mono text-[10px] text-muted-foreground">
                slug: {slugify(displayName)}
              </span>
            )}
          </label>
        </div>
      </Panel>

      <Panel>
        <div className="space-y-3 p-3">
          <p className="text-[11px] font-medium">
            Why this platform may hold a file on this subject
          </p>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Legal basis
            </span>
            <select value={legalBasisCode} onChange={(e) => setLegalBasisCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              {LEGAL_BASES.map((b) => <option key={b} value={b}>{label(b)}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Purpose
            </span>
            <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={2}
                      placeholder="In a sentence: what this file is for."
                      className="rounded border border-input bg-background px-2 py-1 text-[12px]" />
            <span className={`text-[10px] ${purposeTooShort ? 'text-amber-600'
              : 'text-muted-foreground'}`}>
              {purpose.trim().length}/20 characters minimum
            </span>
          </label>
        </div>
      </Panel>

      <Panel>
        <div className="space-y-2 p-3">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={startCollection} className="mt-0.5"
                   onChange={(e) => setStartCollection(e.target.checked)} />
            <span>
              <span className="text-[12px] font-medium">Start collecting on this subject now</span>
              {/* The plainest words available. This is the act, and the screen should say so. */}
              <span className="block text-[11px] text-muted-foreground">
                This begins standing collection against them. It is recorded in the audit trail
                against your name, with the sentence you write below.
              </span>
            </span>
          </label>
          {startCollection && (
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Collection purpose
              </span>
              <textarea value={collectionPurpose} rows={2}
                        onChange={(e) => setCollectionPurpose(e.target.value)}
                        placeholder="Why this platform is collecting on this subject."
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
              <span className={`text-[10px] ${collectionPurposeMissing ? 'text-amber-600'
                : 'text-muted-foreground'}`}>
                {collectionPurpose.trim().length}/20 characters minimum
              </span>
            </label>
          )}
        </div>
      </Panel>

      <div className="flex items-center gap-2">
        <button type="submit"
                disabled={busy || purposeTooShort || collectionPurposeMissing
                          || (!slugMode && !subjectRef.trim())}
                className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                           text-primary-foreground disabled:opacity-50">
          {busy ? 'Opening…' : 'Open the file'}
        </button>
        <button type="button" onClick={() => navigate('/dossier/subjects')}
                className="rounded border border-border px-3 py-1.5 text-[12px]">
          Cancel
        </button>
        {!slugMode && !subjectRef.trim() && (
          <span className="text-[11px] text-muted-foreground">
            {SEARCHERS[typeCode]
              ? `Search for the ${label(typeCode).toLowerCase()} above.`
              : `Paste the ${label(typeCode).toLowerCase()} id above.`}
          </span>
        )}
      </div>
    </form>
  )
}

function slugify(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
